import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button,
  Checkbox,
  Select,
  message,
  Form,
  Input,
  Spin,
} from 'antd';
import DatePicker from '../DatePicker';
import { connect } from 'react-redux';
import { validPhoneNumber } from '../../global/validation';
import ModalWrapper from './ModalWrapper';
import { getRecordResponseById } from '../../actions/replies';
import {
  hasFeatureInLocalStorage,
  REFERRAL_REPLY_DEPARTMENT_FILTER_FEATURE,
} from '../../global/featureFlags';
import { getCachedReferralReplyTemplates } from '../../util/referralTemplateStorage';
import ReferralReplyFaxApproval from './ReferralReplyFaxApproval';
import { replyFaxDestinationApproved } from '../../actions/records';

import './referralReply.scss';

const EMAIL_ATTACHMENT = 'email_attachment';

// The server refuses a reply to a reply fax number nobody has approved yet
// (ENG-3544). Matches the machine code if one is sent, else today's message.
const isReplyFaxNotApprovedError = (errorMessage) => (
  typeof errorMessage === 'string'
  && (errorMessage === 'reply_fax_not_approved' || /not been approved/i.test(errorMessage))
);

// The server refuses a reply to a fax number blocked for the organization.
// Matches the machine code if one is sent, else a message naming the block.
const isReplyFaxBlacklistedError = (errorMessage) => (
  typeof errorMessage === 'string'
  && (errorMessage === 'reply_fax_blacklisted' || /blacklist|is blocked/i.test(errorMessage))
);

// An emailed referral has no fax line of its own; the server refuses when the
// organization has none to send the reply from (ENG-3674).
const isNoSendingFaxLineError = (errorMessage) => errorMessage === 'no_sending_fax_line';

const isInvalidEmailReplyFaxError = (errorMessage) => (
  errorMessage === 'invalid_number' || errorMessage === 'fax_number_unconfirmed'
);

class ReferralReply extends Component {
  constructor(props) {
    super(props);
    const patientFirstName = this.props.modalType.data.patientFirstName || '';
    const patientMiddleName = this.props.modalType.data.patientMiddleName || '';
    const patientLastName = this.props.modalType.data.patientLastName || '';

    const patientName = this.props.modalType.data.patientName
    || [patientFirstName, patientMiddleName, patientLastName].filter(Boolean).join(' ');

    // An emailed referral (ENG-3674) has no sender fax number: the field starts
    // from the number extracted from the document (empty when none), or, on a
    // resend, from the number the earlier reply went to. The user must confirm
    // it before sending.
    const receiverType = this.props.modalType.data.receiverType || 'received_fax';
    const isEmailReferral = receiverType === EMAIL_ATTACHMENT;
    const { caller_name: callerName, extractedReplyFaxNumber, resentReplySlug } = this.props.modalType.data;
    const initialFaxNumber = isEmailReferral && !resentReplySlug ? extractedReplyFaxNumber : callerName;

    this.state = {
      sendToken: this.props.modalType.data.sendToken,
      patientName,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      referringProvider: this.props.modalType.data.referringProvider,
      patientDOB: this.props.modalType.data.patientDOB,
      fileToken: this.props.modalType.data.fileToken,
      ownerType: this.props.modalType.data.ownerType,
      ownerId: this.props.modalType.data.ownerId,
      receiverType,
      isEmailReferral,
      callerName: this.getTenDigitPhoneNumber(initialFaxNumber),
      faxNumberConfirmed: false,
      templates: getCachedReferralReplyTemplates(),
      isPreviewLoading: false,
      resentRecordSlug: this.props.modalType.data.resentRecordSlug,
      resentReplySlug: this.props.modalType.data.resentReplySlug,
      resendDetailsChanged: false,
      isUsingOriginal: false,
      referralReplyUri: this.props.modalType.data.referralReplyUri,
      appointmentDate: this.props.modalType.data.appointmentDate,
      scheduledProvider: this.props.modalType.data.scheduledProvider || '',
      replyFaxDecisionPending: false,
      // The reply number choice's buttons, shown in the footer while it is on screen.
      replyFaxFooterAction: null,
      // Send stays blocked until the reply fax destination lookup settles, so a
      // fast click cannot skip a decision that is still loading. That lookup is
      // ReceivedFax-only, so an emailed referral never waits on it.
      replyFaxLookupPending: !isEmailReferral && Boolean(this.props.modalType.data.sendToken),
      faxNumberTouched: false,
      replyFaxResendRebuild: false,
    };
  }

  componentDidMount = async () => {
    const { getReferralDocumentStatus, modalType } = this.props;
    const { sendToken, fileToken, receiverType } = this.state;

    await getReferralDocumentStatus(fileToken, sendToken, receiverType);

    // A pre-selected template (resend flow) may belong to a different department than the
    // record. With the department filter on it is hidden from the dropdown, so drop it and
    // force a fresh pick — otherwise the submit button stays enabled and would silently
    // resend a wrong-department template.
    const presetTemplate = modalType.data.referralTemplate;
    const presetFilteredOut = !!presetTemplate
      && !this.getVisibleTemplates(presetTemplate).some(template => template.slug === presetTemplate);

    // The reply fax lookup can answer first; if it already switched a resend to
    // a different approved number, the resend stays a rebuilt one (see
    // handleApprovedReplyFaxDestination) instead of "Use original reply".
    this.setState((state) => {
      const rebuild = state.replyFaxResendRebuild;
      const originalReply = modalType.data.resentReplySlug && !rebuild ? 'original_reply' : undefined;
      return {
        selectedTemplate: presetFilteredOut ? undefined : (presetTemplate || originalReply),
        isUsingOriginal: !rebuild && modalType.data.resentReplySlug && !presetTemplate,
        resendDetailsChanged: presetFilteredOut || rebuild,
      };
    });
  }

  // When the department filter feature is on and the record belongs to a department,
  // only show that department's templates plus org-level templates. Records without a
  // department (User/Organization-owned) fall back to showing all accessible templates.
  //
  // Archived templates are hidden UNLESS one is the current selection (a resend
  // preselects the historical reply's template) — then it stays visible but
  // disabled and labeled "(Archived)", mirroring how deactivated referral
  // statuses are shown for referrals still sitting in them. `keepSlug` lets
  // componentDidMount evaluate the preset before selectedTemplate exists.
  getVisibleTemplates = (keepSlug = this.state.selectedTemplate) => {
    const { templates, ownerType, ownerId } = this.state;
    const departmentFilterEnabled = hasFeatureInLocalStorage(
      REFERRAL_REPLY_DEPARTMENT_FILTER_FEATURE,
    );

    // `!== false` so cached auth payloads from before the archive feature
    // (templates without the flag) keep working as active.
    const activeOrKept = templates.filter(template => (
      template.active !== false || template.slug === keepSlug
    ));

    if (departmentFilterEnabled && ownerType === 'Department') {
      return activeOrKept.filter(template => (
        template.owner_type === 'Organization'
        || (template.owner_type === 'Department' && String(template.owner_id) === String(ownerId))
      ));
    }

    return activeOrKept;
  }

  getTenDigitPhoneNumber = (phoneNumber) => {
    return (phoneNumber || '').replace(/\D/g, '').slice(-11);
  };

  handleTemplateChange = (value) => {
    this.setState({ 
      selectedTemplate: value, 
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleFaxNumberChange = (e) => {
    this.setState({
      callerName: e.target.value,
      // A confirmation covers the number it was given for, not an edited one.
      faxNumberConfirmed: false,
      faxNumberTouched: true,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  // A reply fax number approved through ReferralReplyFaxApproval (on load or by
  // an explicit click) becomes the number in the field. A number the user typed
  // while the lookup was loading wins over the on-load prefill; an explicit
  // decision always applies.
  //
  // An unchanged resend duplicates the old reply, including the number it was
  // faxed to. When the approved number differs, the resend counts as changed so
  // the reply is rebuilt for the new number; "Use original reply" has no
  // template to rebuild from, so the user picks one.
  handleApprovedReplyFaxDestination = (faxNumber, { source } = {}) => {
    if (!faxNumber) return;
    this.setState((state) => {
      if (source === 'lookup' && state.faxNumberTouched) return null;

      const approved = this.getTenDigitPhoneNumber(faxNumber);
      const update = { callerName: approved };
      const numberChanged = approved.slice(-10) !== this.getTenDigitPhoneNumber(state.callerName || '').slice(-10);
      if (state.resentReplySlug && numberChanged) {
        update.replyFaxResendRebuild = true;
        update.resendDetailsChanged = true;
        update.isUsingOriginal = false;
        if (state.selectedTemplate === 'original_reply') update.selectedTemplate = undefined;
      }
      return update;
    });
  };

  handleFaxNumberConfirmedChange = (e) => {
    const confirmed = e.target.checked;
    this.setState({ faxNumberConfirmed: confirmed });
  };

  handleReplyFaxLookupSettled = () => {
    this.setState({ replyFaxLookupPending: false });
  };

  handleReplyFaxFooterActionChange = (footerAction) => {
    this.setState({ replyFaxFooterAction: footerAction });
  };

  handleReplyFaxDecisionPendingChange = (pending) => {
    this.setState({ replyFaxDecisionPending: pending });
  };

  handleDateChange = (_, dateString) => {
    this.setState({
      patientDOB: dateString,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleAppointmentDateChange = (_, dateString) => {
    this.setState({
      appointmentDate: dateString === '' ? null : dateString,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleScheduledProviderChange = (e) => {
    this.setState({
      scheduledProvider: e.target.value,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleNameChange = (e) => {
    this.setState({
      patientName: e.target.value,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleFirstNameChange = (e) => {
    this.setState({
      patientFirstName: e.target.value,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  };

  handleMiddleNameChange = (e) => {
    this.setState({
      patientMiddleName: e.target.value,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleLastNameChange = (e) => {
    this.setState({
      patientLastName: e.target.value,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleProviderChange = (e) => {
    this.setState({
      referringProvider: e.target.value,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleDownload = async () => {
    const { getReferralReplyPreviewUrl } = this.props;
    const {
      selectedTemplate,
      sendToken,
      patientDOB,
      patientName,
      referringProvider,
      isUsingOriginal,
      resentRecordSlug,
      resentReplySlug,
      resendDetailsChanged,
      appointmentDate,
      scheduledProvider,
      receiverType,
    } = this.state;

    this.setState({ isPreviewLoading: true });
    const formattedAppointmentDate = appointmentDate && moment(appointmentDate).format('MM/DD/YYYY h:mm A');

    // An unchanged resend duplicates the persisted original document (see
    // handleSend / the server's resend path), so preview that document rather
    // than re-rendering from the template: a fresh render would stamp today's
    // date instead of the original's, and an archived template cannot render
    // new previews at all (the server 404s it) even though resending the
    // unchanged original is still allowed.
    const previewsOriginalDocument = isUsingOriginal || (!!resentReplySlug && !resendDetailsChanged);

    try {
      await getReferralReplyPreviewUrl(
        sendToken,
        patientName,
        patientDOB,
        referringProvider,
        previewsOriginalDocument ? null : selectedTemplate,
        formattedAppointmentDate,
        previewsOriginalDocument ? resentRecordSlug : null,
        scheduledProvider,
        receiverType,
      );
      window.open(this.props.previewUrl.signed_url);
    } catch (error) {
      message.error('Failed to generate preview. Please try again.');
    } finally {
      this.setState({ isPreviewLoading: false });
    }
  }

  handleSend = async () => {
    const {
      hideModal, modalType: { data: { sendToken } },
    } = this.props;
    const { sendReferralReply } = this.props;
    const {
      selectedTemplate,
      callerName,
      patientDOB,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      referringProvider,
      resentReplySlug,
      resendDetailsChanged,
      isUsingOriginal,
      referralReplyUri,
      appointmentDate,
      scheduledProvider,
      receiverType,
      faxNumberConfirmed,
    } = this.state;
    const splitDate = patientDOB.split('/');
    const formattedDOB = `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}`;

    // Convert appointmentDate to UTC before sending
    let utcAppointmentDate = appointmentDate;
    let localAppointmentDate = null;
    if (appointmentDate) {
      const isoMoment = moment(appointmentDate, moment.ISO_8601, true);
      if (!isoMoment.isValid()) { // If we fetch from the DB, it's already in UTC, so this won't run
        utcAppointmentDate = moment(appointmentDate, "MM/DD/YYYY hh:mm A").utc().format("MM/DD/YYYY hh:mm A");
      } else {
        utcAppointmentDate = isoMoment.utc().format("MM/DD/YYYY hh:mm A");
      }
      localAppointmentDate = moment.utc(utcAppointmentDate, "MM/DD/YYYY hh:mm A").local().format("MM/DD/YYYY hh:mm A");
    }
    // Construct full patient name from individual fields
    const fullPatientName = [patientFirstName, patientMiddleName, patientLastName].filter(Boolean).join(' ');

    await sendReferralReply({
      sendToken,
      selectedTemplate: isUsingOriginal ? null : selectedTemplate,
      callerName,
      patientDOB: formattedDOB,
      patientName: fullPatientName,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      referringProvider,
      resentReplySlug,
      resendDetailsChanged,
      appointmentDate: utcAppointmentDate,
      scheduledProvider,
      localAppointmentDate,
      receiverType,
      faxNumberConfirmed,
    });

    const { error, errorCode, errorMessage } = this.props;
    const isEmailReply = receiverType === EMAIL_ATTACHMENT;
    let errorMessageContent = 'Error sending reply. Please try again.';
    if (errorCode === 422 && isReplyFaxNotApprovedError(errorMessage)) {
      errorMessageContent = 'This fax number needs approval before sending. Open Reply again to approve it or keep the current number. If no approval prompt appears, contact your Admin.';
    } else if (errorCode === 422 && isInvalidEmailReplyFaxError(errorMessage)) {
      errorMessageContent = 'Enter and confirm a valid fax number.';
    } else if (errorCode === 422 && isNoSendingFaxLineError(errorMessage)) {
      errorMessageContent = 'Your organization has no fax line to send this reply from. Contact your Admin.';
    } else if (errorCode === 422 && isReplyFaxBlacklistedError(errorMessage)) {
      errorMessageContent = 'This fax number is blocked for your organization, so replies can\'t be sent to it. Use a different number or contact your Admin.';
    } else if (errorCode === 422 && isEmailReply) {
      // The address-book advice below is about a received fax's own number; an
      // emailed referral has none, so its other refusals get a neutral message.
      errorMessageContent = 'The reply couldn\'t be sent. Check the details and try again.';
    } else if (errorCode === 422) {
      errorMessageContent = 'You must create an address book contact for the received fax phone number before you can reply to the fax.';
    } else if (errorCode === 404 && isEmailReply) {
      // The server 404s an emailed referral it will not reply to: the org flag was
      // turned off mid-session or the attachment is gone (ENG-3674).
      errorMessageContent = 'This emailed referral can\'t be replied to right now. Refresh the page and try again, or contact your Admin.';
    } else if (errorCode === 404) {
      // The template list is a sign-in-time localStorage snapshot, so a template
      // archived since this session started still renders as selectable here.
      // The server refuses to render a new reply from it (404) and retrying can
      // never succeed — the generic "please try again" sent users into a loop
      // with nothing pointing at the real cause or the way out.
      errorMessageContent = 'That referral reply template is no longer available — it may have been archived. Refresh the page, then choose another template.';
    }
    hideModal();
    await this.props.getRecordResponseById(referralReplyUri);
    if (!error) {
      message.success({
        content: 'Referral Reply will be sent shortly.',
      });
    } else {
      message.error({
        content: errorMessageContent,
      });
    }
  }

  render() {
    const { isLoading, hideModal } = this.props;
    const {
      selectedTemplate,
      templates,
      callerName,
      patientDOB,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      referringProvider,
      resentReplySlug,
      resendDetailsChanged,
      isUsingOriginal,
      appointmentDate,
      scheduledProvider,
      replyFaxDecisionPending,
      replyFaxFooterAction,
      replyFaxLookupPending,
      replyFaxResendRebuild,
      sendToken,
      isEmailReferral,
      faxNumberConfirmed,
    } = this.state;
    const { extractedReplyFaxEvidence } = this.props.modalType.data;

    const visibleTemplates = this.getVisibleTemplates();

    const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];
    const validatePhoneNumber = (rule, value) => {
      if (!value) {
        return Promise.reject('Please enter the fax number!');
      } if (!validPhoneNumber(value)) {
        return Promise.reject('Please enter a valid fax number!');
      }
      return Promise.resolve();
    };

    // An archived template can stay preselected on a resend (shown disabled in
    // the dropdown), but it must never be submittable for a NEW render — the
    // server would 404 it. The unchanged-resend path (isUsingOriginal or
    // resendDetailsChanged=false) re-sends the historical reply without
    // rendering the template, so it stays allowed.
    const selectedIsArchived = templates.some(
      (template) => template.slug === selectedTemplate && template.active === false,
    );
    // Whether the user has any way out from inside the modal: an active option
    // they could switch to. Drives which explanation is shown below.
    const hasActiveVisibleTemplate = visibleTemplates.some((template) => template.active !== false);

    const isSubmitButtonDisabled = () => {
      // A reply fax number found in the document is waiting for a person to
      // approve it or keep the current number; sending before that, or before
      // the lookup has answered, is blocked.
      if (replyFaxDecisionPending || replyFaxLookupPending) {
        return true;
      }

      if (isUsingOriginal) {
        return false;
      }

      const selectionIsValid = !selectedTemplate
        || selectedIsArchived
        || callerName === null || callerName === ''
        || patientDOB === null || patientDOB === ''
        || patientFirstName === null || patientFirstName === ''
        || patientLastName === null || patientLastName === ''
        || referringProvider === null || referringProvider === '';

      return resentReplySlug ? (resendDetailsChanged && selectionIsValid) : (selectionIsValid || !validPhoneNumber(callerName));
    };

    // Preview sends nothing, so only Send waits for the emailed-referral confirmation.
    const isSendDisabled = () => isSubmitButtonDisabled() || (isEmailReferral && !faxNumberConfirmed);

    const customContentStyle = {
      width: '270px',
    };

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        // While the reply number is being chosen the footer carries the
        // choice's Continue; Send returns with the form.
        action={replyFaxDecisionPending ? replyFaxFooterAction?.onClick : this.handleSend}
        actionName={replyFaxDecisionPending ? (replyFaxFooterAction?.name || null) : 'Send'}
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={replyFaxDecisionPending ? Boolean(replyFaxFooterAction?.disabled) : isSendDisabled()}
        isLoading={Boolean(replyFaxDecisionPending && replyFaxFooterAction?.loading)}
        footerNote={replyFaxDecisionPending && replyFaxFooterAction?.secondary ? (
          <Button type="link" className="referral-reply-modal__secondary" onClick={replyFaxFooterAction.secondary.onClick}>
            {replyFaxFooterAction.secondary.name}
          </Button>
        ) : null}
        modalTitle="Send Referral Reply"
        form="referralReply"
        width="550px"
        wrapClassName="referral-reply-modal-wrap"
        isDraggable
      >
        <div>
          {/*
            A reply fax number found in the document that still needs a decision
            takes over the window: the decision step shows in place of the reply
            form, and the form (with the fax number filled in) comes back once
            the person approves it or keeps the current number.
          */}
          {/* Reply fax approval is ReceivedFax-only; an emailed referral confirms below. */}
          {!isEmailReferral && (
            <ReferralReplyFaxApproval
              sendToken={sendToken}
              onApprovedDestination={this.handleApprovedReplyFaxDestination}
              onDecisionPendingChange={this.handleReplyFaxDecisionPendingChange}
              onLookupSettled={this.handleReplyFaxLookupSettled}
              onApproved={this.props.replyFaxDestinationApproved}
              onFooterActionChange={this.handleReplyFaxFooterActionChange}
            />
          )}
          {replyFaxLookupPending && (
            <div
              className="referral-reply-fax-approval__note"
              style={{ padding: '0 10px' }}
              data-testid="reply-fax-lookup-pending"
            >
              Checking reply fax number…
            </div>
          )}
          {!replyFaxDecisionPending && (
          <div style={{ marginBottom: '20px', padding: '10px' }}>
            {!isUsingOriginal && visibleTemplates.length === 0 && (
              <div style={{ color: 'red', marginBottom: '10px' }}>
                {templates.filter((template) => template.active !== false).length === 0
                  ? '* Your Organization does not have any referral reply templates. Please contact your Admin.'
                  : '* No referral reply templates are available for this department. Please contact your Admin.'}
              </div>
            )}
            {/*
              Resending an archived template's reply is allowed only while the
              details are unchanged (the server re-sends the stored document and
              never re-renders the template). Once anything is edited, Send and
              Preview are correctly locked -- but without this line nothing on
              screen said why, and `resendDetailsChanged` is one-way, so undoing
              the edit does not bring Send back. Say so, and name the exit.
            */}
            {/*
              A resend rebuilt for a newly approved reply fax number can never go
              out unchanged (reopening rebuilds it again), so that exit is not
              offered there.
            */}
            {selectedIsArchived && resendDetailsChanged && !isUsingOriginal && (
              <div style={{ color: 'red', marginBottom: '10px' }}>
                {replyFaxResendRebuild && (hasActiveVisibleTemplate
                  ? '* This template has been archived. The reply has to be rebuilt to fax the approved number, so choose another template to continue.'
                  : '* This template has been archived and your Organization has no other available template, so the reply cannot be rebuilt for the approved number. Contact your Admin.')}
                {!replyFaxResendRebuild && (hasActiveVisibleTemplate
                  ? '* This template has been archived, so it can no longer be used for an edited reply. Choose another template to continue, or reopen this reply and resend it unchanged.'
                  : '* This template has been archived and your Organization has no other available template, so an edited reply cannot be sent. Close this window and resend the reply unchanged, or contact your Admin.')}
              </div>
            )}
            <div style={{ marginBottom: '5px', padding: '10px' }}>
              {/* Template and Fax Number Section */}
              <div style={{ display: 'flex', gap: '20px', marginBottom: '5px' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '5px', color: 'black' }}>Referral Template</label>
                  <Select
                    style={{ width: 300 }}
                    onChange={this.handleTemplateChange}
                    value={selectedTemplate}
                  >
                    {/* A rebuilt resend has no original reply to fall back to. */}
                    {resentReplySlug && !this.props.modalType.data.referralTemplate && !replyFaxResendRebuild && (
                      <Select.Option value="original_reply">
                        &lt;Original Reply&gt;
                      </Select.Option>
                    )}
                    {visibleTemplates.map(template => (
                      <Select.Option
                        key={template.slug}
                        value={template.slug}
                        disabled={template.active === false}
                        style={template.active === false ? { color: '#999' } : {}}
                      >
                        {`${template.template_name}${template.active === false ? ' (Archived)' : ''}`}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '5px', color: 'black' }}>Fax Number</label>
                  <Form.Item
                    rules={[
                      {
                        validator: validatePhoneNumber,
                      },
                    ]}
                    onChange={this.handleFaxNumberChange}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      placeholder="Enter Fax number"
                      name="faxnumber"
                      value={this.getTenDigitPhoneNumber(callerName)}
                      style={{ width: 130 }}
                      disabled={isUsingOriginal}
                    />
                    {callerName && !validPhoneNumber(callerName) && <div style={{ color: 'red' }}>Invalid fax number</div>}
                  </Form.Item>
                </div>
              </div>

              {isEmailReferral && (
                <div
                  className="referral-reply-fax-approval"
                  style={{ padding: 0, marginBottom: '10px' }}
                  data-testid="email-reply-fax-confirmation"
                >
                  {(extractedReplyFaxEvidence?.supporting_quote || extractedReplyFaxEvidence?.page_number) && (
                    <div className="referral-reply-fax-approval__evidence" data-testid="email-reply-fax-evidence">
                      {extractedReplyFaxEvidence.supporting_quote && (
                        <blockquote>{`“${extractedReplyFaxEvidence.supporting_quote}”`}</blockquote>
                      )}
                      <div className="referral-reply-fax-approval__source">
                        {extractedReplyFaxEvidence.page_number
                          ? `From this document · Page ${extractedReplyFaxEvidence.page_number}`
                          : 'From this document'}
                      </div>
                    </div>
                  )}
                  <Checkbox
                    checked={faxNumberConfirmed}
                    onChange={this.handleFaxNumberConfirmedChange}
                  >
                    Replies contain PHI. Confirm this is the referring office&apos;s fax number.
                  </Checkbox>
                </div>
              )}

              {/* Patient Name and DOB Section */}
              <div style={{ display: 'flex', gap: '20px', marginBottom: '5px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <label style={{ marginBottom: '5px', color: 'black' }}>Patient Name</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                      <Input
                        value={patientFirstName}
                        placeholder="First name"
                        onChange={this.handleFirstNameChange}
                        disabled={isUsingOriginal}
                      />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                      <Input
                        value={patientMiddleName}
                        placeholder="Middle name"
                        onChange={this.handleMiddleNameChange}
                        disabled={isUsingOriginal}
                      />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                      <Input
                        value={patientLastName}
                        placeholder="Last name"
                        onChange={this.handleLastNameChange}
                        disabled={isUsingOriginal}
                      />
                    </Form.Item>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '5px', color: 'black' }}>Patient Date of Birth</label>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <DatePicker
                      onChange={this.handleDateChange}
                      name="patientDOB"
                      format={dateFormat}
                      defaultValue={patientDOB && moment(patientDOB, dateFormat)}
                      value={patientDOB && moment(patientDOB, dateFormat)}
                      disabled={isUsingOriginal}
                    />
                  </Form.Item>
                </div>
              </div>

              {/* Referring Provider and Appointment Date Section */}
              <div style={{ display: 'flex', gap: '20px', marginBottom: '5px' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '5px', color: 'black' }}>Referring Provider</label>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Input
                      value={referringProvider}
                      placeholder="Referring Provider Name"
                      onChange={this.handleProviderChange}
                      style={{ width: 255 }}
                    />
                  </Form.Item>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '5px', color: 'black' }}>Appointment Date</label>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <DatePicker 
                      showTime
                      onChange={this.handleAppointmentDateChange}
                      name="appointmentDate"
                      format="MM/DD/YYYY hh:mm A"
                      defaultValue={appointmentDate && moment(appointmentDate)}
                      value={appointmentDate && moment(appointmentDate)}
                      style={{ width: 180 }}
                      disabled={isUsingOriginal}
                    />
                  </Form.Item>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                <label htmlFor="scheduledProvider" style={{ marginBottom: '5px', color: 'black' }}>Scheduled Provider (optional)</label>
                <Form.Item style={{ marginBottom: 0 }}>
                  <Input
                    id="scheduledProvider"
                    value={scheduledProvider}
                    onChange={this.handleScheduledProviderChange}
                    disabled={isUsingOriginal}
                  />
                </Form.Item>
              </div>
            </div>

            <div style={{ marginLeft: '10px', position: 'relative' }}>
              <Button
                type="primary"
                disabled={isSubmitButtonDisabled() || this.state.isPreviewLoading}
                onClick={this.handleDownload}
                style={{
                  backgroundColor: '#691b9a',
                  borderColor: '#691b9a',
                  color: 'white',
                  opacity: (isSubmitButtonDisabled() || this.state.isPreviewLoading) ? 0.6 : 1,
                }}
              >
                {this.state.isPreviewLoading ? 'Generating Preview...' : 'Preview Referral Reply'}
              </Button>
              {this.state.isPreviewLoading && (
                <Spin style={{ marginLeft: '10px' }} />
              )}
            </div>
          </div>
          )}
        </div>
      </ModalWrapper>
    );
  }
}

ReferralReply.defaultProps = {
  data: {},
  error: false,
  errorCode: '',
  errorMessage: null,
  replyFaxDestinationApproved: () => {},
};

ReferralReply.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.bool,
  errorCode: PropTypes.string,
  errorMessage: PropTypes.string,
  replyFaxDestinationApproved: PropTypes.func,
  data: PropTypes.shape({
    referral_reply: PropTypes.object,
  }),
  getReferralDocumentStatus: PropTypes.func.isRequired,
  sendReferralReply: PropTypes.func.isRequired,
  getReferralReplyPreviewUrl: PropTypes.func.isRequired,
  previewUrl: PropTypes.shape({
    signed_url: PropTypes.string.isRequired,
  }),
};

export default connect(state => ({
  previewUrl: state.records.previewUrl,
  error: state.records.error,
  errorCode: state.records.errorCode,
  errorMessage: state.records.errorMessage,
}), {
  getRecordResponseById,
  replyFaxDestinationApproved,
})(ReferralReply);
